import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
} from 'react';

export interface HelpButton {
  label: string;
  url: string;
}

export interface HelpCard {
  title: string;
  description: string;
  button: HelpButton;
}

export interface HelpContent {
  cards: HelpCard[];
}

interface HelpContextType {
  helpContent: HelpContent | null;
  setHelpContent: (content: HelpContent | null) => void;
  isPanelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  // Px to shift the Help panel + floating "?" to the left so another right-anchored
  // side panel (e.g., Transformation Rules) can sit on the outer edge.
  rightOffsetPx: number;
  setRightOffsetPx: (px: number) => void;
  // True while a side panel wants the floating "?" out of the way entirely —
  // shifting is not enough when the panel's own footer sits where the button floats.
  fabSuppressed: boolean;
  setFabSuppressed: (suppressed: boolean) => void;
}

const HelpContext = createContext<HelpContextType | undefined>(undefined);

type HelpProviderProps = {
  children: ReactNode;
};

export const HelpProvider: React.FC<HelpProviderProps> = ({ children }) => {
  const [helpContent, setHelpContentState] = useState<HelpContent | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [rightOffsetPx, setRightOffsetPx] = useState(0);
  const [fabSuppressed, setFabSuppressed] = useState(false);

  const openPanel = useCallback(() => setIsPanelOpen(true), []);
  const closePanel = useCallback(() => setIsPanelOpen(false), []);

  const setHelpContent = useCallback((content: HelpContent | null) => {
    setHelpContentState(content);
    if (!content) {
      setIsPanelOpen(false);
    }
  }, []);

  return (
    <HelpContext.Provider
      value={{
        helpContent,
        setHelpContent,
        isPanelOpen,
        openPanel,
        closePanel,
        rightOffsetPx,
        setRightOffsetPx,
        fabSuppressed,
        setFabSuppressed,
      }}
    >
      {children}
    </HelpContext.Provider>
  );
};

export const useHelp = (): HelpContextType => {
  const context = useContext(HelpContext);
  if (context === undefined) {
    throw new Error('useHelp must be used within a HelpProvider');
  }
  return context;
};
